import { Button } from '@/components/ui/button';
import Typography from '@/components/ui/typography';
import { reportClientError } from '@/lib/reportClientError';
import { Component, ErrorInfo, PropsWithChildren } from 'react';

interface State {
  hasError: boolean;
}

/**
 * Wraps the whole Inertia app so a render crash anywhere (including inside
 * AppLayout/AdminLayout) shows a plain fallback instead of a blank page, and
 * gets reported to client.log — production client errors were otherwise
 * completely invisible.
 */
class ErrorBoundary extends Component<PropsWithChildren, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportClientError({
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack ?? undefined,
    });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface px-5 text-center">
        <Typography.H2>Something went wrong</Typography.H2>
        <Typography.Subtle className="max-w-md">
          Please refresh the page. If the problem keeps happening, contact us
          and let us know what you were doing.
        </Typography.Subtle>
        <Button onClick={() => window.location.reload()}>Refresh page</Button>
      </div>
    );
  }
}

export default ErrorBoundary;
